import { LitElement, html, css } from 'lit';
import { customElement, property, state } from 'lit/decorators.js';
import { styles } from '../../dashboard.style.js';
import { Firestore } from 'firebase/firestore';
import { getPartsByCompany } from '@cmms/api';

@customElement('inventory-mini-dashboard')
export class InventoryMiniDashboard extends LitElement {
  @property({ type: String }) companyId = '';
  @property({ type: Object }) firestore: Firestore = null as any;

  @state() private parts: Array<any> = [];
  @state() private totalParts = 0;
  @state() private lowStock = 0;
  @state() private outOfStock = 0;
  @state() private totalValue = 0;
  @state() private avgValue = 0;
  @state() private topStockValue: Array<{ name: string; value: number }> = [];
  @state() private lowStockList: Array<{ name: string; stock_qty: number; reorder_level: number } > = [];

  static styles = [
    styles,
    css`
      .mini-dashboard { display:flex; flex-direction:column; gap:1.5rem; background: var(--sl-color-neutral-50); padding:1rem; }
      .mini-header { display:flex; align-items:center; gap:1rem; }
      .bar-list { display:flex; flex-direction:column; gap:0.5rem; }
      .bar-row { display:flex; align-items:center; gap:0.75rem; }
      .bar { flex:1; height:10px; background: var(--sl-color-neutral-200); border-radius:999px; overflow:hidden; }
      .bar > div { height:100%; background: var(--sl-color-primary-500); }
      .label { min-width: 160px; color: var(--sl-color-neutral-700); overflow: hidden; text-overflow: ellipsis; white-space: nowrap; }
      .value { width: 40px; text-align:right; color: var(--sl-color-neutral-800); font-variant-numeric: tabular-nums; }
      .data-table { width:100%; border-collapse:collapse; }
      .data-table th, .data-table td { padding:0.75rem; text-align:left; border-bottom:1px solid var(--sl-color-neutral-200); }
      .data-table th { color: var(--sl-color-neutral-600); font-weight:600; font-size:0.875rem; }
      .data-table td { color: var(--sl-color-neutral-900); font-size:0.875rem; }
    `
  ];

  connectedCallback(): void {
    super.connectedCallback();
    this.loadData();
  }

  updated(changed: Map<string, unknown>): void {
    if (changed.has('companyId')) this.loadData();
  }

  private async loadData() {
    if (!this.companyId || !this.firestore) return;
    try {
      const resp: any = await getPartsByCompany(this.firestore, this.companyId);
      const partsRaw: any[] = Array.isArray(resp?.data) ? resp.data : (Array.isArray(resp) ? resp : []);

      // Normalize to a consistent shape (model-aware)
      const normalized = (partsRaw || []).map(p => ({
        id: p.id,
        name: p.model?.name || p.name || 'Part',
        stock_qty: p.model?.stock_qty ?? p.stock_qty ?? 0,
        reorder_level: p.model?.reorder_level ?? p.reorder_level ?? 0,
        cost: p.model?.cost ?? p.cost ?? 0
      }));
      this.parts = normalized;

      // Enhanced KPIs
      const totalParts = normalized.length;
      const lowStock = normalized.filter(p => (p.stock_qty || 0) <= (p.reorder_level || 0)).length;
      const outOfStock = normalized.filter(p => (p.stock_qty || 0) <= 0).length;
      const totalStockValue = normalized.reduce((sum, p) => sum + (Number(p.stock_qty) || 0) * (Number(p.cost) || 0), 0);
      const avgStockValue = totalParts ? totalStockValue / totalParts : 0;
      const topStockValue = [...normalized]
        .sort((a, b) => ((b.stock_qty || 0) * (b.cost || 0)) - ((a.stock_qty || 0) * (a.cost || 0)))
        .slice(0, 5)
        .map(p => ({ name: p.name, value: (Number(p.stock_qty) || 0) * (Number(p.cost) || 0) }));
      const lowStockList = normalized
        .filter(p => (p.stock_qty || 0) <= (p.reorder_level || 0))
        .sort((a, b) => (a.stock_qty || 0) - (b.stock_qty || 0))
        .slice(0, 5)
        .map(p => ({ name: p.name, stock_qty: p.stock_qty || 0, reorder_level: p.reorder_level || 0 }));

      this.totalParts = totalParts;
      this.lowStock = lowStock;
      this.outOfStock = outOfStock;
      this.totalValue = totalStockValue;
      this.avgValue = avgStockValue;
      this.topStockValue = topStockValue;
      this.lowStockList = lowStockList;
      this.requestUpdate();
    } catch (err) {
      console.error('Failed to load parts:', err);
      this.parts = [];
      this.totalParts = 0;
      this.lowStock = 0;
      this.outOfStock = 0;
      this.totalValue = 0;
      this.avgValue = 0;
      this.topStockValue = [];
      this.lowStockList = [];
    }
  }

  private formatCurrency(n: number) {
    return new Intl.NumberFormat('en-US', { style: 'currency', currency: 'USD' }).format(n || 0);
  }

  render() {
    const stockCategories = [
      { label: 'Low', count: this.lowStock },
      { label: 'Normal', count: Math.max(0, this.totalParts - this.lowStock - this.outOfStock) },
      { label: 'Out', count: this.outOfStock }
    ];
    return html`
      <div class="mini-dashboard">
        <div class="mini-header" style="display:flex;align-items:center;gap:0.75rem;margin-bottom:1rem;">
          <h2 style="margin:0;font-size:1.25rem;">Inventory Overview</h2>
        </div>

        <div class="overview-cards">
          <div class="overview-card"><h3>Total Parts</h3><p class="value">${this.totalParts}</p></div>
          <div class="overview-card"><h3>Low Stock</h3><p class="value">${this.lowStock}</p></div>
          <div class="overview-card"><h3>Out of Stock</h3><p class="value">${this.outOfStock}</p></div>
          <div class="overview-card"><h3>Total Stock Value</h3><p class="value">${this.formatCurrency(this.totalValue)}</p></div>
          <div class="overview-card"><h3>Average Value / Part</h3><p class="value">${this.formatCurrency(this.avgValue)}</p></div>
        </div>

        <div class="widget">
          <h3>Low Stock Items</h3>
          <div style="overflow-x:auto">
            <table class="data-table">
              <thead><tr><th>Part Name</th><th>Quantity</th><th>Reorder Level</th></tr></thead>
              <tbody>
                ${this.lowStockList.map(p => html`
                  <tr>
                    <td>${p.name}</td>
                    <td>${p.stock_qty}</td>
                    <td>${p.reorder_level}</td>
                  </tr>
                `)}
              </tbody>
            </table>
          </div>
        </div>

        <div class="widget">
          <h3>Top 5 Parts by Stock Value</h3>
          <div class="bar-list">
            ${(() => {
              const max = Math.max(1, ...this.topStockValue.map(f => f.value));
              return this.topStockValue.map(f => html`
                <div class="bar-row">
                  <div class="label">${f.name}</div>
                  <div class="bar"><div style="width:${Math.round(((f.value || 0) / max) * 100)}%"></div></div>
                  <div class="value">${this.formatCurrency(f.value || 0)}</div>
                </div>
              `);
            })()}
          </div>
        </div>

        <div class="widget">
          <h3>Stock Distribution</h3>
          <div class="bar-list">
            ${(() => {
              const max = Math.max(1, ...stockCategories.map(s => s.count));
              return stockCategories.map(s => html`
                <div class="bar-row">
                  <div class="label">${s.label}</div>
                  <div class="bar"><div style="width:${Math.round(((s.count || 0) / max) * 100)}%"></div></div>
                  <div class="value">${s.count}</div>
                </div>
              `);
            })()}
          </div>
        </div>
      </div>
    `;
  }
}

declare global {
  interface HTMLElementTagNameMap {
    'inventory-mini-dashboard': InventoryMiniDashboard;
  }
}
